import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { TabView, TabPanel } from "primereact/tabview";
import { ProgressBar } from "primereact/progressbar";
import { Badge } from "primereact/badge";
import { Knob } from "primereact/knob";
import { Avatar } from "primereact/avatar";
import { Calendar } from "primereact/calendar";
import { renewalMockData, renewalCrudOperations } from "../../../services/mockData/renewalMockData";
import SvgDot from "../../../assets/icons/SvgDot";
import "./index.scss";

const PerformanceTracking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('Current Month');
  const [teamFilter, setTeamFilter] = useState('All Teams');
  const [productFilter, setProductFilter] = useState('All Products');
  const [dateRange, setDateRange] = useState([null, null]);
  const [performanceData, setPerformanceData] = useState(renewalMockData.performanceMetrics);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const toast = useRef(null);

  const timeFilterOptions = [
    { label: 'Current Month', value: 'Current Month' },
    { label: 'Last 3 Months', value: 'Last 3 Months' },
    { label: 'Last 6 Months', value: 'Last 6 Months' },
    { label: 'Year to Date', value: 'Year to Date' },
    { label: 'Custom Range', value: 'Custom Range' }
  ];

  const teamFilterOptions = [
    { label: 'All Teams', value: 'All Teams' },
    { label: 'Renewal Team A', value: 'Team A' },
    { label: 'Renewal Team B', value: 'Team B' },
    { label: 'Senior Agents', value: 'Senior' },
    { label: 'Junior Agents', value: 'Junior' }
  ];

  const productFilterOptions = [
    { label: 'All Products', value: 'All Products' },
    { label: 'Motor Insurance', value: 'motor' },
    { label: 'Fire Insurance', value: 'fire' },
    { label: 'Marine Insurance', value: 'marine' },
    { label: 'Health Insurance', value: 'health' },
    { label: 'Personal Accident', value: 'personalAccident' }
  ];

  const items = [
    { label: "Renewals", url: "#" },
    { label: "Performance Tracking", url: "#" }
  ];

  const home = { icon: <SvgDot />, url: "#" };

  useEffect(() => {
    loadPerformanceData();
    setupCharts();
  }, [timeFilter, teamFilter, productFilter]);

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      // Simulate loading performance data based on filters
      setTimeout(() => {
        setPerformanceData(renewalMockData.performanceMetrics);
        setLoading(false);
        toast.current.show({
          severity: 'success',
          summary: 'Data Loaded',
          detail: 'Performance data updated successfully',
          life: 3000
        });
      }, 1000);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load performance data',
        life: 3000
      });
      setLoading(false);
    }
  };

  const setupCharts = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const primaryColor = '#3B82F6';
    const successColor = '#10B981';
    const warningColor = '#F59E0B';
    const dangerColor = '#EF4444';

    // Performance Trend Chart
    const trendData = {
      labels: performanceData.trends?.monthly?.map(item => item.month) || [],
      datasets: [
        {
          label: 'Renewal Rate (%)',
          data: performanceData.trends?.monthly?.map(item => item.rate) || [],
          borderColor: primaryColor,
          backgroundColor: `${primaryColor}20`,
          tension: 0.4,
          fill: true
        }
      ]
    };

    // Agent Performance Comparison
    const agentComparisonData = {
      labels: performanceData.byAgent?.map(agent => agent.agentName.split(' ')[0]) || [],
      datasets: [
        {
          label: 'Renewal Rate (%)',
          data: performanceData.byAgent?.map(agent => agent.renewalRate) || [],
          backgroundColor: primaryColor,
          borderRadius: 4
        },
        {
          label: 'Premium Retained (M)',
          data: performanceData.byAgent?.map(agent => agent.premiumRetained / 1000000) || [],
          backgroundColor: successColor,
          borderRadius: 4,
          yAxisID: 'y1'
        }
      ]
    };

    // Product Performance Pie Chart
    const productPerformanceData = {
      labels: ['Motor', 'Fire', 'Marine', 'Health', 'Personal Accident'],
      datasets: [
        {
          data: [
            performanceData.byProduct?.motor?.renewalRate || 0,
            performanceData.byProduct?.fire?.renewalRate || 0,
            performanceData.byProduct?.marine?.renewalRate || 0,
            performanceData.byProduct?.health?.renewalRate || 0,
            performanceData.byProduct?.personalAccident?.renewalRate || 0
          ],
          backgroundColor: [primaryColor, successColor, warningColor, dangerColor, '#8B5CF6'],
          borderWidth: 0
        }
      ]
    };

    setChartData({
      trend: trendData,
      agentComparison: agentComparisonData,
      productPerformance: productPerformanceData
    });

    const baseOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      }
    };

    setChartOptions({
      trend: {
        ...baseOptions,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        }
      },
      agentComparison: {
        ...baseOptions,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              callback: function(value) {
                return '₱' + value + 'M';
              }
            }
          }
        }
      },
      productPerformance: {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          legend: {
            position: 'right'
          }
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value, decimals = 1) => {
    return `${value?.toFixed(decimals) || 0}%`;
  };

  const agentRankingTemplate = (rowData) => {
    const getRankIcon = (rank) => {
      switch (rank) {
        case 1: return '🥇';
        case 2: return '🥈';
        case 3: return '🥉';
        default: return `#${rank}`;
      }
    };

    const getRankColor = (rank) => {
      switch (rank) {
        case 1: return 'gold';
        case 2: return 'silver';
        case 3: return 'bronze';
        default: return 'default';
      }
    };

    return (
      <div className={`rank-cell ${getRankColor(rowData.ranking)}`}>
        <span className="rank-icon">{getRankIcon(rowData.ranking)}</span>
      </div>
    );
  };

  const agentNameTemplate = (rowData) => {
    return (
      <div className="agent-cell">
        <Avatar
          label={rowData.agentName.split(' ').map(n => n[0]).join('')}
          size="normal"
          shape="circle"
          className="agent-avatar"
        />
        <div className="agent-info">
          <span className="agent-name">{rowData.agentName}</span>
          <small className="agent-team">Renewal Team</small>
        </div>
      </div>
    );
  };

  const renewalRateTemplate = (rowData) => {
    const rate = rowData.renewalRate || 0;
    const getColor = (rate) => {
      if (rate >= 90) return 'success';
      if (rate >= 80) return 'info';
      if (rate >= 70) return 'warning';
      return 'danger';
    };

    return (
      <div className="rate-cell">
        <ProgressBar value={rate} className={`rate-progress ${getColor(rate)}`} />
        <span className={`rate-value ${getColor(rate)}`}>{formatPercentage(rate)}</span>
      </div>
    );
  };

  const premiumRetainedTemplate = (rowData) => {
    return (
      <div className="premium-cell">
        <span className="premium-amount">{formatCurrency(rowData.premiumRetained)}</span>
        <small className="premium-policies">{rowData.policiesRenewed} policies</small>
      </div>
    );
  };

  const cycleTimeTemplate = (rowData) => {
    const days = rowData.avgCycleTime;
    const getSeverity = (days) => {
      if (days <= 14) return 'success';
      if (days <= 21) return 'warning';
      return 'danger';
    };

    return <Tag value={`${days} days`} severity={getSeverity(days)} />;
  };

  const productRateTemplate = (rowData, field) => {
    const product = performanceData.byProduct?.[field];
    if (!product) return '-';

    return (
      <div className="product-rate-cell">
        <span className="rate">{formatPercentage(product.renewalRate)}</span>
        <small className="avg-premium">{formatCurrency(product.avgPremium)}</small>
      </div>
    );
  };

  // Sample KPI targets and achievements
  const kpiData = [
    {
      category: 'Renewal Rate',
      target: 85,
      achieved: performanceData.overall?.renewalRate || 82.5,
      unit: '%'
    },
    {
      category: 'Premium Retention',
      target: 90,
      achieved: performanceData.overall?.premiumRetention || 87.3,
      unit: '%'
    },
    {
      category: 'Cycle Time',
      target: 15,
      achieved: performanceData.overall?.avgCycleTime || 18,
      unit: 'days',
      inverse: true // Lower is better
    },
    {
      category: 'Customer Satisfaction',
      target: 4.5,
      achieved: performanceData.overall?.customerSatisfaction || 4.2,
      unit: '/5'
    }
  ];

  const kpiAchievementTemplate = (rowData) => {
    const percentage = rowData.inverse
      ? Math.max(0, 100 - ((rowData.achieved - rowData.target) / rowData.target) * 100)
      : (rowData.achieved / rowData.target) * 100;

    const getSeverity = (percentage) => {
      if (percentage >= 100) return 'success';
      if (percentage >= 90) return 'info';
      if (percentage >= 80) return 'warning';
      return 'danger';
    };

    return (
      <div className="kpi-achievement">
        <ProgressBar value={Math.min(percentage, 100)} className={getSeverity(percentage)} />
        <span className="achievement-text">
          {percentage >= 100 ? '✓ Achieved' : `${Math.round(percentage)}% of target`}
        </span>
      </div>
    );
  };

  return (
    <div className="container__performance__tracking__master">
      <Toast ref={toast} />

      <div className="top__container">
        <h1 className="page__title">Performance Tracking</h1>
        <BreadCrumb model={items} home={home} />
      </div>

      <div className="content-container">
        {/* Filters Section */}
        <div className="filters-section">
          <Card>
            <div className="filters-grid">
              <div className="filter-field">
                <label>Time Period</label>
                <Dropdown
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.value)}
                  options={timeFilterOptions}
                />
              </div>

              <div className="filter-field">
                <label>Team</label>
                <Dropdown
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.value)}
                  options={teamFilterOptions}
                />
              </div>

              <div className="filter-field">
                <label>Product Line</label>
                <Dropdown
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.value)}
                  options={productFilterOptions}
                />
              </div>

              {timeFilter === 'Custom Range' && (
                <div className="filter-field">
                  <label>Date Range</label>
                  <Calendar
                    value={dateRange}
                    onChange={(e) => setDateRange(e.value)}
                    selectionMode="range"
                    dateFormat="mm/dd/yy"
                  />
                </div>
              )}

              <div className="filter-actions">
                <Button
                  label="Apply Filters"
                  icon="pi pi-filter"
                  onClick={loadPerformanceData}
                  loading={loading}
                />
                <Button
                  label="Export Report"
                  icon="pi pi-file-excel"
                  className="p-button-secondary"
                  onClick={() => {
                    toast.current.show({
                      severity: 'success',
                      summary: 'Export Started',
                      detail: 'Performance report exported to Excel',
                      life: 3000
                    });
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* KPI Overview */}
        <div className="kpi-overview">
          <Card>
            <div className="kpi-header">
              <h3>Key Performance Indicators</h3>
              <Badge value="Current Period" severity="info" />
            </div>
            <div className="kpi-grid">
              {kpiData.map((kpi, index) => (
                <div key={index} className="kpi-item">
                  <div className="kpi-visual">
                    <Knob
                      value={kpi.inverse
                        ? Math.max(0, 100 - ((kpi.achieved - kpi.target) / kpi.target) * 100)
                        : Math.min((kpi.achieved / kpi.target) * 100, 100)}
                      size={80}
                      readOnly
                      valueColor={
                        (kpi.inverse
                          ? kpi.achieved <= kpi.target
                          : kpi.achieved >= kpi.target)
                        ? "#10B981" : "#EF4444"
                      }
                      rangeColor="#E5E7EB"
                    />
                  </div>
                  <div className="kpi-details">
                    <span className="kpi-category">{kpi.category}</span>
                    <div className="kpi-values">
                      <span className="achieved">{kpi.achieved}{kpi.unit}</span>
                      <span className="target">Target: {kpi.target}{kpi.unit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Performance Charts */}
        <div className="performance-charts">
          <TabView>
            <TabPanel header="Trend Analysis">
              <div className="charts-container">
                <Card className="trend-chart">
                  <div className="chart-header">
                    <h3>Renewal Rate Trend</h3>
                    <Badge value="12 months" severity="info" />
                  </div>
                  <Chart
                    type="line"
                    data={chartData.trend}
                    options={chartOptions.trend}
                    height="300px"
                  />
                </Card>
              </div>
            </TabPanel>

            <TabPanel header="Agent Performance">
              <div className="charts-container">
                <div className="charts-grid">
                  <Card className="comparison-chart">
                    <div className="chart-header">
                      <h3>Agent Performance Comparison</h3>
                      <Badge value="Dual metrics" severity="info" />
                    </div>
                    <Chart
                      type="bar"
                      data={chartData.agentComparison}
                      options={chartOptions.agentComparison}
                      height="300px"
                    />
                  </Card>

                  <Card className="leaderboard-card">
                    <div className="chart-header">
                      <h3>Agent Leaderboard</h3>
                    </div>
                    <DataTable
                      value={performanceData.byAgent || []}
                      className="leaderboard-table"
                      showGridlines={false}
                    >
                      <Column
                        body={agentRankingTemplate}
                        header="Rank"
                        style={{ width: '60px', textAlign: 'center' }}
                      />
                      <Column
                        body={agentNameTemplate}
                        header="Agent"
                        style={{ width: '180px' }}
                      />
                      <Column
                        body={renewalRateTemplate}
                        header="Renewal Rate"
                        style={{ width: '120px' }}
                      />
                      <Column
                        body={premiumRetainedTemplate}
                        header="Premium"
                        style={{ width: '140px' }}
                      />
                      <Column
                        body={cycleTimeTemplate}
                        header="Cycle Time"
                        style={{ width: '100px' }}
                      />
                    </DataTable>
                  </Card>
                </div>
              </div>
            </TabPanel>

            <TabPanel header="Product Performance">
              <div className="charts-container">
                <div className="charts-grid">
                  <Card className="product-chart">
                    <div className="chart-header">
                      <h3>Product Performance Distribution</h3>
                      <Badge value="Renewal rates" severity="info" />
                    </div>
                    <Chart
                      type="doughnut"
                      data={chartData.productPerformance}
                      options={chartOptions.productPerformance}
                      height="300px"
                    />
                  </Card>

                  <Card className="product-metrics">
                    <div className="chart-header">
                      <h3>Product Metrics Summary</h3>
                    </div>
                    <DataTable
                      value={[{ id: 1 }]} // Single row for product metrics
                      className="product-table"
                      showGridlines={false}
                      header={null}
                    >
                      <Column field="product" header="Product" body={() => 'Motor'} />
                      <Column body={(data) => productRateTemplate(data, 'motor')} header="Motor" />
                      <Column body={(data) => productRateTemplate(data, 'fire')} header="Fire" />
                      <Column body={(data) => productRateTemplate(data, 'marine')} header="Marine" />
                      <Column body={(data) => productRateTemplate(data, 'health')} header="Health" />
                      <Column body={(data) => productRateTemplate(data, 'personalAccident')} header="PA" />
                    </DataTable>
                  </Card>
                </div>
              </div>
            </TabPanel>

            <TabPanel header="KPI Scorecard">
              <div className="scorecard-container">
                <Card>
                  <div className="chart-header">
                    <h3>Performance Scorecard</h3>
                    <Badge value="vs Targets" severity="info" />
                  </div>
                  <DataTable
                    value={kpiData}
                    className="scorecard-table"
                    showGridlines={false}
                  >
                    <Column
                      field="category"
                      header="KPI Category"
                      style={{ width: '25%' }}
                    />
                    <Column
                      field="target"
                      header="Target"
                      style={{ width: '15%' }}
                      body={(data) => `${data.target}${data.unit}`}
                    />
                    <Column
                      field="achieved"
                      header="Achieved"
                      style={{ width: '15%' }}
                      body={(data) => `${data.achieved}${data.unit}`}
                    />
                    <Column
                      body={kpiAchievementTemplate}
                      header="Achievement"
                      style={{ width: '45%' }}
                    />
                  </DataTable>
                </Card>

                <div className="scorecard-summary">
                  <Card>
                    <div className="summary-header">
                      <h3>Performance Summary</h3>
                    </div>
                    <div className="summary-metrics">
                      <div className="summary-item">
                        <span className="metric-label">KPIs Achieved</span>
                        <span className="metric-value success">
                          {kpiData.filter(kpi =>
                            kpi.inverse
                              ? kpi.achieved <= kpi.target
                              : kpi.achieved >= kpi.target
                          ).length}/{kpiData.length}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="metric-label">Overall Score</span>
                        <span className="metric-value info">
                          {Math.round(
                            kpiData.reduce((total, kpi) => {
                              const achievement = kpi.inverse
                                ? Math.max(0, 100 - ((kpi.achieved - kpi.target) / kpi.target) * 100)
                                : (kpi.achieved / kpi.target) * 100;
                              return total + Math.min(achievement, 100);
                            }, 0) / kpiData.length
                          )}%
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="metric-label">Top Performer</span>
                        <span className="metric-value primary">
                          {performanceData.byAgent?.[0]?.agentName || 'Ana Reyes'}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="metric-label">Improvement Area</span>
                        <span className="metric-value warning">Cycle Time</span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </TabPanel>
          </TabView>
        </div>

        {/* Performance Insights */}
        <div className="performance-insights">
          <Card>
            <div className="insights-header">
              <h3>Performance Insights & Recommendations</h3>
              <Badge value="AI Generated" severity="success" />
            </div>
            <div className="insights-grid">
              <div className="insight-item positive">
                <div className="insight-icon">
                  <i className="pi pi-thumbs-up"></i>
                </div>
                <div className="insight-content">
                  <h4>Strong Overall Performance</h4>
                  <p>Renewal rate of 82.5% is above industry average. Ana Reyes leads with 88.2% success rate.</p>
                </div>
              </div>

              <div className="insight-item warning">
                <div className="insight-icon">
                  <i className="pi pi-clock"></i>
                </div>
                <div className="insight-content">
                  <h4>Cycle Time Optimization</h4>
                  <p>Average cycle time of 18 days exceeds target. Consider process automation for faster renewals.</p>
                </div>
              </div>

              <div className="insight-item info">
                <div className="insight-icon">
                  <i className="pi pi-chart-line"></i>
                </div>
                <div className="insight-content">
                  <h4>Marine Insurance Opportunity</h4>
                  <p>Marine insurance has lowest renewal rate at 75%. Focus on retention strategies for this segment.</p>
                </div>
              </div>

              <div className="insight-item success">
                <div className="insight-icon">
                  <i className="pi pi-star"></i>
                </div>
                <div className="insight-content">
                  <h4>Customer Satisfaction Growth</h4>
                  <p>Satisfaction improved to 4.2/5. Continue focus on customer experience excellence.</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTracking;